import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import logger from '../lib/logger';
import { supabase, AuthUserWithProfile } from '../lib/supabase';
import {
  signInWithEmail,
  signOut,
  getCurrentSession,
  getCurrentUser,
  getUserProfile,
  getUserOrganization,
  getUserRole,
  isEmailVerified,
  refreshSession,
  onAuthStateChange,
  DEMO_ORGANIZATION
} from '../services/authService';
import { setRLSContext } from '../services/rlsContextService';
import { DEMO_CONFIG } from '../demo/demoUsers';

interface UseAuthReturn {
  // State
  user: AuthUserWithProfile | null;
  session: unknown | null;
  profile: ReturnType<typeof getUserProfile> extends Promise<infer T> ? T : never;
  organization: ReturnType<typeof getUserOrganization> extends Promise<infer T> ? T : never;
  role: string;
  
  // Computed
  isAuthenticated: boolean;
  emailVerified: boolean;
  isLoading: boolean;
  error: string | null;
  rlsReady: boolean; // CRITICAL: Wait for this before making tenant-scoped queries
  
  // Actions
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

/**
 * Hook för att hantera autentisering i CaseFlow+AI
 * 
 * Returnerar:
 * - user: Aktuell användare med profil och organisation
 * - session: Aktuell session
 * - profile: Användarens profil (full_name, role, etc.)
 * - organization: Användarens organisation
 * - role: Användarens roll (admin, technician, agent, viewer)
 * - isAuthenticated: Boolean som indikerar om användaren är inloggad
 * - isEmailVerified: Boolean som indikerar om email är verifierad
 * - isLoading: Boolean som indikerar pågående laddning
 * - signIn: Funktion för att logga in
 * - signOut: Funktion för att logga ut
 * - refreshSession: Funktion för att uppdatera session
 */
export const useAuth = (): UseAuthReturn => {
  // FAS 2: DEV-only debug logging
  if (import.meta.env.DEV) {
    console.log('[DEV AUTH] Hook initialized');
  }
  
  const [user, setUser] = useState<AuthUserWithProfile | null>(null);
  const [session, setSession] = useState<unknown | null>(null);
  const [profile, setProfile] = useState<ReturnType<typeof getUserProfile> extends Promise<infer T> ? T : never>(null);
  const [organization, setOrganization] = useState<ReturnType<typeof getUserOrganization> extends Promise<infer T> ? T : never>(null);
  
  // Ref to track if we have a valid organization (to prevent overwrite to null)
  const hasValidOrgRef = useRef(false);
  // Ref to track user state without causing fetchUserData to be recreated
  const userRef = useRef<AuthUserWithProfile | null>(null);
  const [role, setRole] = useState<string>('viewer');
  const [emailVerified, setEmailVerified] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [rlsReady, setRlsReady] = useState<boolean>(false); // RLS context readiness guard

  // Helper to update both user state and ref (prevents stale closures)
  const setUserWithRef = (newUser: AuthUserWithProfile | null) => {
    userRef.current = newUser;
    setUser(newUser);
  };

  // Hämta användardata (profil + organisation)
  // FIX 406: sessionToken parameter is required to avoid race condition with getSession()
  const fetchUserData = useCallback(async (userId: string, existingUser?: AuthUserWithProfile, sessionToken?: string) => {
    try {
      setIsLoading(true);
      
      // FAS 3 & 4: For demo users, NORMALIZE auth shape and NEVER overwrite organization to null
      const isDemoUser = userId.startsWith('demo-');
      
      if (isDemoUser && import.meta.env.DEV) {
        // FAS 4: For demo users, FORCE set DEMO_ORGANIZATION
        // This ensures organization is NEVER null for demo users
        const demoUserWithOrg = {
          ...(existingUser || userRef.current),
          organization_id: DEMO_ORGANIZATION.id,
          organization: DEMO_ORGANIZATION,
        };
        
        setOrganization(DEMO_ORGANIZATION);
        setUserWithRef(demoUserWithOrg as AuthUserWithProfile);
        hasValidOrgRef.current = true;
        
        // Still fetch profile for role information
        const profileData = await getUserProfile(userId);
        setProfile(profileData as never);
        
        const userRole = profileData?.role || 'viewer';
        setRole(userRole);
      
        setEmailVerified(true);
        
        // Set RLS context for demo (skip - no RLS needed)
        // For demo mode, we set rlsReady to true immediately
        setRlsReady(true);
        
        // FAS 2: DEV-only debug
        if (import.meta.env.DEV) {
          console.log('[DEV AUTH] fetchUserData DEMO', {
            user: demoUserWithOrg,
            organization: DEMO_ORGANIZATION,
            organizationId: DEMO_ORGANIZATION.id,
            userOrganization: demoUserWithOrg.organization,
            organizationIdFromUser: demoUserWithOrg.organization_id,
            role: userRole,
            isAuthenticated: true,
          });
        }
        
        logger.audit('Demo användardata hämtad', {
          userId: userId.substring(0, 8) + '...',
          role: userRole,
          hasOrganization: true,
          isDemo: true,
        });
        
        setIsLoading(false);
        return;
      }
      
      // FIX 406: Pass sessionToken explicitly to avoid getSession() race condition
      // This ensures profile/org queries have the access_token immediately after login
      const [profileData, organizationData, emailVerified] = await Promise.all([
        getUserProfile(userId, sessionToken),
        getUserOrganization(userId, sessionToken),
        isEmailVerified(userId),
      ]);

      setProfile(profileData as never);
      
      // Don't overwrite organization to null if we already have a valid one
      if (organizationData) {
        setOrganization(organizationData as never);
        hasValidOrgRef.current = true;
      } else if (!hasValidOrgRef.current) {
        setOrganization(null as never);
      }
      
      setEmailVerified(emailVerified);
      
      // FAS 2: DEV-only debug
      if (import.meta.env.DEV) {
        console.log('[DEV AUTH] fetchUserData', {
          userId: userId.substring(0, 8) + '...',
          hasOrganization: !!organizationData,
          organizationId: organizationData?.id,
          isDemoUser,
        });
      }
      
      // Sätt roll från profil eller default 'viewer'
      const userRole = profileData?.role || 'viewer';
      setRole(userRole);
      
      // CRITICAL: Set RLS context after organization and role are known
      if (organizationData && userRole) {
        const rlsResult = await setRLSContext({
          organizationId: organizationData.id,
          userRole,
          environment: 'production',
        });
        setRlsReady(rlsResult.success);
        
        if (!rlsResult.success) {
          logger.warn('RLS context setting failed, tenant-scoped queries may be unsafe', {
            error: rlsResult.error,
            organizationId: organizationData.id?.substring(0, 8) + '...',
            role: userRole,
          });
        }
      } else {
        // No organization or role - RLS context cannot be set
        setRlsReady(false);
        logger.warn('Cannot set RLS context: missing organization or role', {
          hasOrganization: !!organizationData,
          hasRole: !!userRole,
        });
      }
      
      // Update user object with organization if not already set
      if (existingUser && organizationData && !existingUser.organization) {
        setUserWithRef({
          ...existingUser,
          organization: organizationData,
        });
      }
      
      logger.audit('Användardata hämtad', {
        userId: userId.substring(0, 8) + '...',
        role: userRole,
        hasOrganization: !!organizationData,
        email_verified: emailVerified,
      });
    } catch (err) {
      logger.error('Fel vid hämtning av användardata', { error: String(err) });
      setRole('viewer'); // Säker default
      setEmailVerified(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initiera auth state
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Hämta aktuell session
        const currentSession = await getCurrentSession();
        
        if (!mounted) return;

        if (currentSession) {
          setSession(currentSession);
           
         // Hämta användare
         const currentUser = await getCurrentUser();
           
         if (currentUser) {
           const authUser: AuthUserWithProfile = {
             id: currentUser.id,
             email: currentUser.email || '',
             organization: currentUser.organization,
             profile: currentUser.profile,
             email_verified: currentUser.email_verified,
           };
            
           setUserWithRef(authUser);
            
           // FIX 406: Pass session token to avoid getSession() race condition
           const sessionToken = currentSession?.access_token;
           await fetchUserData(currentUser.id, authUser, sessionToken);
          }
        } else {
          setUserWithRef(null);
          setProfile(null as never);
          setOrganization(null as never);
          setRole('viewer');
          setEmailVerified(false);
          setRlsReady(false);
          setIsLoading(false);
        }
      } catch (err) {
        if (!mounted) return;
        
        logger.error('Fel vid initiering av auth', { error: String(err) });
        setError('Kunde inte hämta inloggningsstatus');
        setIsLoading(false);
      }
    };

    initializeAuth();

    // CRITICAL FIX: Track the last successful SIGNED_IN timestamp to prevent transient session clearing
    // This adds a debounce guard - we ignore SIGNED_OUT/null events for 750ms after a successful SIGNED_IN
    // This allows time for Supabase to properly load the session from localStorage
    let lastSignedInTime = 0;
    const SESSION_DEBOUNCE_MS = 750;
    
    // DIAGNOSTICS: Track auth state changes
    console.log('AUTH_LISTENER_REGISTERED', { timestamp: Date.now() });
    
    // Lyssna på auth-state ändringar
    const { data: { subscription } } = onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      // DIAGNOSTICS: Log auth state change details
      console.log('AUTH_STATE_CHANGE_LISTENER', {
        event,
        hasSession: !!session,
        lastSignedInTime,
        timestamp: Date.now()
      });
      
      logger.audit('Auth state change', { event, hasSession: !!session, lastSignedInTime });
      
      // DIAGNOSTICS: Check localStorage in listener
      const storedAuth = localStorage.getItem('caseflow-auth');
      const customAuth = localStorage.getItem('demo-session');
      console.log('AUTH_LISTENER_STORAGE_CHECK', {
        supabaseAuth: !!storedAuth,
        demoSession: !!customAuth,
        supabaseAuthLength: storedAuth?.length || 0
      });
      
      if (session) {
        // Update the last SIGNED_IN timestamp
        lastSignedInTime = Date.now();
        setSession(session);
         
        // Extract user from session
        const sessionUser = (session as any)?.user;
        if (sessionUser) {
          const authUser: AuthUserWithProfile = {
            id: sessionUser.id,
            email: sessionUser.email || '',
            organization: sessionUser.organization,
            profile: sessionUser.profile,
            email_verified: sessionUser.email_verified,
          };
           
          setUserWithRef(authUser);
          
          // FIX 406: Pass session token to avoid getSession() race condition
          const sessionToken = session?.access_token;
          await fetchUserData(sessionUser.id, authUser, sessionToken);
          
          // Set RLS context after auth state change
          if (organization && role) {
            await setRLSContext({
              organizationId: organization.id,
              userRole: role,
              environment: 'production',
            });
          }
        } else if (supabase) {
          // Only try to get user if supabase is configured
          const { data: { user: currentUser } } = await supabase.auth.getUser();
           
          if (currentUser) {
            const authUser: AuthUserWithProfile = {
              id: currentUser.id,
              email: currentUser.email || '',
            };
             
            setUserWithRef(authUser);
            
            // FIX 406: Pass session token to avoid getSession() race condition
            const sessionToken = session?.access_token;
            await fetchUserData(currentUser.id, authUser, sessionToken);
          }
        } else {
          // Supabase not configured, just set session without user details
          logger.info('Supabase inte konfigurerat, kan inte hämta användardetaljer', {});
        }
      } else {
        // CRITICAL FIX: Debounce guard - ignore transient SIGNED_OUT events
        // Check if this is a transient event (within debounce window after SIGNED_IN)
        const timeSinceSignedIn = Date.now() - lastSignedInTime;
        
        if (lastSignedInTime > 0 && timeSinceSignedIn < SESSION_DEBOUNCE_MS) {
          // This is a transient event - check Supabase session before clearing
          logger.audit('AUTH_TRANSIENT_SIGNED_OUT', { timeSinceSignedIn, debounceMs: SESSION_DEBOUNCE_MS });
          
          // Try to get the session directly from Supabase
          if (supabase) {
            const { data: { session: freshSession } } = await supabase.auth.getSession();
            
            // DIAGNOSTICS: Log getSession result in debounce
            console.log('GETSESSION_IN_DEBOUNCE', !!freshSession);
            
            if (freshSession) {
              logger.audit('AUTH_SESSION_RESTORED_FROM_STORAGE', { userId: freshSession.user?.id?.substring(0, 8) + '...' || 'unknown' });
              // Session was restored - don't clear state
              lastSignedInTime = Date.now();
              setSession(freshSession);
              return;
            }
          }
          
          // No session found after debounce - log and proceed with clearing
          logger.audit('AUTH_SESSION_CLEARED_AFTER_DEBOUNCE', { timeSinceSignedIn });
        }
        
        // CRITICAL FIX: Check for demo session only as FALLBACK after Supabase session check
        // This prevents demo mode from interfering with real Pilot sessions
        if (import.meta.env.DEV || DEMO_CONFIG.enabled) {
          // Try to restore session using getCurrentSession (which now checks Supabase first)
          const restoredSession = await getCurrentSession();
          
          if (restoredSession) {
            logger.audit('AUTH_STATE_CHANGE_RESTORING_SESSION', {
              event,
              userId: (restoredSession as any)?.user?.id?.substring(0, 8) + '...'
            });
            
            setSession(restoredSession);
            
            const sessionUser = (restoredSession as any)?.user;
            if (sessionUser) {
              const authUser: AuthUserWithProfile = {
                id: sessionUser.id,
                email: sessionUser.email || '',
                organization: sessionUser.organization,
                profile: sessionUser.profile,
                email_verified: sessionUser.email_verified,
              };
              
              setUserWithRef(authUser);
              
              // Restore organization if available
              if (sessionUser.organization) {
                setOrganization(sessionUser.organization);
                hasValidOrgRef.current = true;
              }
              
              setRole(sessionUser.profile?.role || 'viewer');
              setRlsReady(true);
              setIsLoading(false);
              return;
            }
          }
        }
        
        // No session available, proceed to clear
        if (user && user.id?.startsWith('demo-')) {
          // Don't clear demo user data - keep organization
          logger.info('Demo user session ended, preserving demo state', {});
          return;
        }
        
        // Clear all state for real Pilot users
        setUserWithRef(null);
        setSession(null);
        setProfile(null as never);
        setOrganization(null as never);
        setRole('viewer');
        setEmailVerified(false);
        setRlsReady(false);
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserData]);

  // Sign in funktion
  const handleSignIn = useCallback(async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await signInWithEmail(email, password);
      
      if (result.success && result.user) {
        setUserWithRef(result.user);
        setSession(await getCurrentSession());
        
        // FAS 3: For demo users, ensure organization is set correctly
        const isDemoUser = result.user.id?.startsWith('demo-');
        if (isDemoUser && import.meta.env.DEV) {
          // Force set DEMO_ORGANIZATION for demo users
          const userWithOrg = {
            ...result.user,
            organization_id: DEMO_ORGANIZATION.id,
            organization: DEMO_ORGANIZATION,
          };
          setUserWithRef(userWithOrg as AuthUserWithProfile);
          setOrganization(DEMO_ORGANIZATION);
          hasValidOrgRef.current = true;
        } else if (result.user.organization) {
          setOrganization(result.user.organization);
        }
        
        // Hämta användardata
        // FIX 406: Pass accessToken to avoid getSession() race condition
        if (result.user.id) {
          await fetchUserData(result.user.id, result.user, result.accessToken);
        }
        
        return { success: true };
      } else {
        setError(result.error || 'Inloggning misslyckades');
        setIsLoading(false);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Oväntat fel vid inloggning';
      logger.error(errorMsg, { error: String(err) });
      setError(errorMsg);
      setIsLoading(false);
      return { success: false, error: errorMsg };
    }
  }, [fetchUserData]);

  // Sign out funktion
  const handleSignOut = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      await signOut();
      
      setUserWithRef(null);
      setSession(null);
      setProfile(null as never);
      setOrganization(null as never);
      setRole('viewer');
      setEmailVerified(false);
      hasValidOrgRef.current = false;
      
      logger.audit('Användare utloggad via useAuth', {});
    } catch (err) {
      logger.error('Fel vid utloggning', { error: String(err) });
      setError('Kunde inte logga ut');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh session funktion
  const handleRefreshSession = useCallback(async () => {
    try {
      setIsLoading(true);
      const currentSession = await refreshSession();
       
      if (currentSession) {
        setSession(currentSession);
          
        const currentUser = await getCurrentUser();
        if (currentUser) {
          const authUser: AuthUserWithProfile = {
            id: currentUser.id,
            email: currentUser.email || '',
            organization: currentUser.organization,
            profile: currentUser.profile,
            email_verified: currentUser.email_verified,
          };
          setUserWithRef(authUser);
          
          // FIX 406: Pass session token to avoid getSession() race condition
          const sessionToken = currentSession?.access_token;
          await fetchUserData(currentUser.id, authUser, sessionToken);
        }
      }
    } catch (err) {
      logger.error('Fel vid uppdatering av session', { error: String(err) });
    } finally {
      setIsLoading(false);
    }
  }, [fetchUserData]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Computed values
  const isAuthenticated = useMemo(() => {
    return !!user;
  }, [user]);

  return {
    // State
    user,
    session,
    profile,
    organization,
    role,
    
    // Computed
    isAuthenticated,
    emailVerified,
    isLoading,
    error,
    rlsReady,
    
    // Actions
    signIn: handleSignIn,
    signOut: handleSignOut,
    refreshSession: handleRefreshSession,
    clearError,
  };
};

export default useAuth;
